import { useState } from "react";
import type { CreateStoreResponse, StoreKind, SubscriptionPlanView } from "@/lib/api/types";
import { Button, Drawer, SegmentedControl } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { StoreCreateForm } from "./StoreCreateForm";

const FORM_ID = "store-create-form";

/** "Add business" for an existing seller: an online store or a restaurant. */
export function StoreCreateDrawer({
  open,
  onClose,
  onCreated,
  sellerId,
  sellerName,
  locale,
  plans,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreateStoreResponse) => void;
  sellerId: string;
  sellerName: string;
  locale: Locale;
  plans: SubscriptionPlanView[];
}) {
  const t = useTranslations();
  const [storeKind, setStoreKind] = useState<StoreKind>("DEFAULT");
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setStoreKind("DEFAULT");
    setSubmitting(false);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      closeLabel={t(keys.common.close)}
      width={560}
      crumb={`${sellerName} / ${t(keys.admin.createStoreTitle)}`}
      badge="cart-world"
      title={t(keys.admin.createStoreTitle)}
      sub={t(keys.admin.createStoreSubtitle)}
      actions={
        <Button
          variant="primary"
          size="sm"
          type="submit"
          form={FORM_ID}
          loading={submitting}
          loadingLabel={t("admin.businessCreating")}
        >
          {t(
            storeKind === "RESTAURANT"
              ? keys.admin.restaurantCreateAction
              : keys.admin.onlineStoreCreateAction,
          )}
        </Button>
      }
    >
      <SegmentedControl<StoreKind>
        label={t(keys.admin.businessTypeLabel)}
        value={storeKind}
        onChange={setStoreKind}
        options={[
          { value: "DEFAULT", label: t(keys.admin.onlineStoresTitle) },
          { value: "RESTAURANT", label: t(keys.admin.restaurantsTitle) },
        ]}
      />
      <StoreCreateForm
        key={storeKind}
        formId={FORM_ID}
        sellerId={sellerId}
        locale={locale}
        plans={plans}
        storeKind={storeKind}
        onSubmittingChange={setSubmitting}
        onSuccess={(result) => {
          setStoreKind("DEFAULT");
          setSubmitting(false);
          onCreated(result);
        }}
      />
    </Drawer>
  );
}
